import type { FeatureSummary } from "@tdelivr/api-client/generated/configuration/feature-management.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { asDay, type BoardRow } from "./board.ts";

/**
 * What the feature flags board does to the summaries CFG-02 returns, away from any rendering.
 *
 * Built to `17 WP16 Tenant Admin.dc.html`, page `flags`, against CFG-02 `GET /v1/features` —
 * published by [ADR 0045](../../../docs/adr/0045-a-tenants-feature-flags-are-listed-under-v1-as-a-summary-without-their-allocation-seed.md)
 * because every operation of that service was under `/internal/v1` and its one read took the
 * `flagKey` this board exists to discover.
 *
 * **The state is derived from the contract's own evaluation order, not from a status field.** There
 * is no status: `state` is `"published"` on every row and is deliberately not published for that
 * reason. What decides whether a flag is on is `evaluateFeature` in CFG-02's domain, and it answers
 * in a fixed order — kill switch, then the first matching targeting rule, then the percentage
 * rollout when it is above zero, then `defaultEnabled`. So the six readings in `stateOf` are that
 * order read back, and the two that look surprising are the two that matter:
 *
 *   - **`0%` does not mean off.** With no percentage rollout the default decides, so a flag at zero
 *     with `defaultEnabled` is on for everyone. The design's `cash_on_delivery · 0% · off` row is
 *     the other case, and a board that read the percentage alone would have called them the same.
 *   - **A rule can switch a flag on with the rollout at zero.** `evaluateFeature` returns
 *     `rule.enabled` for the first rule the subject matches, before the rollout is consulted at
 *     all.
 *
 * **`Expires` has no source, and it is one tile, one badge word and one form field.** The design's
 * fourth row is `old_checkout · 100% · expires 15 Oct`, badged `caution · remove soon`, its
 * `Expiring · 1 · clean up` tile counts the same thing, and its `New feature flag` form has an
 * `Expires` date whose own helper text says flags without one are flagged for clean-up. CFG-02
 * holds no expiry: a version carries a description, two booleans, a rollout, a seed, its rules and
 * its approval evidence. Nothing anywhere publishes one. Q-151, and the board draws neither the
 * word nor a cell for it.
 *
 * **The seed is withheld on purpose and the board does not want it.** `allocationSeed` is the value
 * `stableBucket` hashes with a subject key, so a board holding it could say which customers are in
 * a 25% rollout — which is a thing no screen draws and an attacker would like. ADR 0045 §2.
 */

/** The design's own rollout grammar: `100%`, `25%`, `0%`. */
const percentage = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 2 });

export function rolloutText(feature: FeatureSummary): string {
  return `${percentage.format(feature.rolloutBasisPoints / 100)}%`;
}

/** The targeting rules a flag is switched **on** by, which is not all of its rules. */
function includedRules(feature: FeatureSummary): FeatureSummary["targetingRules"] {
  return feature.targetingRules.filter((rule) => rule.enabled);
}

/** The targeting rules a flag is switched **off** by, which the design has no column for. */
function excludedRules(feature: FeatureSummary): FeatureSummary["targetingRules"] {
  return feature.targetingRules.filter((rule) => !rule.enabled);
}

/** One rule as the design writes an audience: its values, joined. */
function ruleText(rule: FeatureSummary["targetingRules"][number]): string {
  return rule.values.join(" · ");
}

/**
 * The design's `Audience` column: `All riders · Nairobi`, `Riders · Kampala`, `Parcels`, `All`.
 *
 * The values of the rules that switch the flag on, joined by the design's own middle dot — and
 * `All` where there are none, which is the design's own word for a flag with no targeting and is
 * exactly what `evaluateFeature` does when no rule matches.
 *
 * A rule whose `enabled` is false is **not** part of the audience: it is an exemption, because
 * `evaluateFeature` returns that rule's own `enabled` for a subject matching it. Those appear in
 * the detail panel and in this cell's explanation rather than in the audience, which would
 * otherwise read as "on for Nairobi" about the one place it is off.
 */
export function audienceText(feature: FeatureSummary): string {
  const included = includedRules(feature);
  return included.length === 0 ? "All" : included.map(ruleText).join(" · ");
}

/**
 * The state class and the word, read off CFG-02's own evaluation order.
 *
 * Three of the design's four words are reachable — `on`, `rolling out`, `off` — and the fourth,
 * `remove soon`, needs the expiry Q-151 records as absent. The two extra readings below are states
 * the design has no word for and the contract certainly has: a flag whose kill switch is set, and a
 * flag that is on only for an audience.
 */
export function stateOf(feature: FeatureSummary): {
  classKey: StateClassKey;
  label: string;
  title?: string;
} {
  if (feature.killSwitch) {
    return {
      classKey: "failed",
      label: "killed",
      title:
        "The kill switch is set, so every evaluation answers false whatever the rollout and the targeting rules say. The design has no word for this state and CFG-02 publishes a kill switch on every flag.",
    };
  }
  if (feature.rolloutBasisPoints >= 10_000) {
    return {
      classKey: "done",
      label: "on",
      ...(excludedRules(feature).length > 0
        ? {
            title:
              "Rolled out to everyone, except the subjects a disabled targeting rule matches — those are answered false before the rollout is consulted.",
          }
        : {}),
    };
  }
  if (feature.rolloutBasisPoints > 0) return { classKey: "active", label: "rolling out" };
  if (feature.defaultEnabled) {
    return {
      classKey: "done",
      label: "on",
      title:
        "No percentage rollout, so the default decides and the default is enabled. A rollout of 0% is not off.",
    };
  }
  if (includedRules(feature).length > 0) {
    return {
      classKey: "active",
      label: "on for an audience",
      title:
        "No percentage rollout and the default is disabled, so this flag is on only for the subjects its enabled targeting rules match — those are answered before the rollout is consulted.",
    };
  }
  return { classKey: "closed", label: "off" };
}

/** One row of the flags board. */
export function flagRow(feature: FeatureSummary): BoardRow {
  const excluded = excludedRules(feature);
  return {
    // The flag key, which is what this tenant's flags are unique by and what the cursor pages over.
    id: feature.flagKey,
    cells: [
      { text: feature.flagKey, mono: true },
      {
        text: audienceText(feature),
        ...(excluded.length > 0
          ? {
              title: `Switched off for ${excluded.map(ruleText).join(" · ")}, which the design's Audience column has no room for.`,
            }
          : {}),
      },
      { text: rolloutText(feature) },
    ],
    state: stateOf(feature),
    /*
     * Two lines where the design's panel lists three, and the third is Q-151's expiry.
     *
     * `description` is published and is the one sentence a flag carries about itself, which is worth
     * more on this panel than it would be as a fourth column on a board whose rows are keys.
     */
    detail: [
      { label: "Description", value: feature.description },
      ...(excluded.length === 0
        ? []
        : [{ label: "Switched off for", value: excluded.map(ruleText).join(" · ") }]),
    ],
    history: [
      { label: "Published", value: asDay(feature.publishedAt) },
      { label: "Version", value: `v${String(feature.version)}` },
    ],
    // The design's own form icon for this page, which is where its detail panel takes the glyph.
    glyph: "toggle_on",
  };
}

/** The states this listing can actually answer, in the order `stateOf` decides them. */
export const FLAG_STATES: readonly string[] = [
  "killed",
  "on",
  "rolling out",
  "on for an audience",
  "off",
];

/** Which flags a chosen state leaves on the board. Client-side: the listing has no filter. */
export function filterFlags(
  features: readonly FeatureSummary[],
  state: string,
): readonly FeatureSummary[] {
  if (state === "") return features;
  return features.filter((feature) => stateOf(feature).label === state);
}
